import * as React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { Container } from '../../../components/web/container/container';
import { Headline } from '../../../components/app/headline/headline';
import { Logo } from '../../../components/app/logo/logo';
import { cn } from '../../../utils/tailwind';
import { FeatureDemo, FeatureKey } from './feature-demos';
import { AccordionIndicator } from '../../../components/web/accordion-indicator/accordion-indicator';

const features: { key: FeatureKey; translation: string; number: string }[] = [
    { key: 'performance', translation: 'feature1', number: '01' },
    { key: 'dividends', translation: 'feature2', number: '02' },
    { key: 'allocation', translation: 'feature3', number: '03' },
    { key: 'tax', translation: 'feature4', number: '04' },
    { key: 'watchlist', translation: 'feature5', number: '05' },
    { key: 'alerts', translation: 'feature6', number: '06' },
];

interface PreviewProps {
    feature: FeatureKey;
    id: string;
}

const FeaturePreview: React.FC<PreviewProps> = ({ feature, id }) => {
    const { t } = useTranslation();
    const reducedMotion = useReducedMotion();

    return (
        <div
            id={id}
            className="min-w-0 overflow-hidden rounded-[2rem] border border-[#d9cdc5] bg-transparent text-text"
        >
            <div
                aria-hidden="true"
                className="relative flex h-[3.4rem] items-center gap-[0.5rem] border-b border-[#d9cdc5] bg-transparent px-[1.6rem]"
            >
                <span className="h-[0.7rem] w-[0.7rem] rounded-full bg-[#b9aea6]" />
                <span className="h-[0.7rem] w-[0.7rem] rounded-full bg-[#b9aea6]" />
                <span className="h-[0.7rem] w-[0.7rem] rounded-full bg-[#b9aea6]" />
                <span className="absolute left-1/2 -translate-x-1/2 text-[1rem] text-[#8c8178]">monery.io</span>
            </div>
            <div className="flex min-h-[6.4rem] items-center justify-between gap-[1rem] border-b border-border bg-surface px-[1.6rem] sm:px-[2.4rem]">
                <div className="origin-left scale-[0.8]">
                    <Logo inApp gradientId={`${id}-logo`} />
                </div>
                <span className="rounded-full bg-brand-secondary/5 px-[1rem] py-[0.5rem] text-[1.1rem] font-medium text-text-muted">
                    {t('pages.homepage.features.demo.sample')}
                </span>
            </div>
            <div className="flex min-w-0 bg-[#f9fbfd] p-[1.2rem] sm:p-[2.4rem]">
                <AnimatePresence initial={false} exitBeforeEnter>
                    <motion.div
                        key={feature}
                        className="flex min-w-0 flex-1 flex-col vl:min-h-[48rem]"
                        initial={{ opacity: 0, y: reducedMotion ? 0 : 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: reducedMotion ? 0 : -4 }}
                        transition={{ duration: reducedMotion ? 0 : 0.18, ease: 'easeOut' }}
                    >
                        <FeatureDemo feature={feature} />
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export const FeatureList: React.FC = () => {
    const { t } = useTranslation();
    const [selected, setSelected] = React.useState<FeatureKey>('performance');
    const reducedMotion = useReducedMotion();
    const scrollFrame = React.useRef(0);
    const previousScrollBehavior = React.useRef<string | null>(null);
    const cancelScroll = React.useCallback(() => {
        window.cancelAnimationFrame(scrollFrame.current);
        if (previousScrollBehavior.current !== null) {
            document.documentElement.style.scrollBehavior = previousScrollBehavior.current;
            previousScrollBehavior.current = null;
        }
    }, []);

    React.useEffect(() => {
        window.addEventListener('touchstart', cancelScroll, { passive: true });
        window.addEventListener('wheel', cancelScroll, { passive: true });
        window.addEventListener('keydown', cancelScroll);
        return () => {
            cancelScroll();
            window.removeEventListener('touchstart', cancelScroll);
            window.removeEventListener('wheel', cancelScroll);
            window.removeEventListener('keydown', cancelScroll);
        };
    }, [cancelScroll]);

    const scrollToFeature = (button: HTMLButtonElement) => {
        cancelScroll();
        if (!window.matchMedia('(max-width: 1023px)').matches) return;
        previousScrollBehavior.current = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        const initialTop = button.getBoundingClientRect().top;
        const targetTop = parseFloat(window.getComputedStyle(button).scrollMarginTop);
        const startedAt = performance.now();
        const duration = reducedMotion ? 0 : 400;
        const updateScroll = (now: number) => {
            const progress = duration ? Math.min(1, (now - startedAt) / duration) : 1;
            const eased = 1 - (1 - progress) ** 3;
            const viewportTop = initialTop + (targetTop - initialTop) * eased;
            // Track the heading as the previous panel collapses, rather than starting a second scroll afterward.
            window.scrollTo({
                top: window.scrollY + button.getBoundingClientRect().top - viewportTop,
                behavior: 'auto',
            });
            if (progress < 1) {
                scrollFrame.current = window.requestAnimationFrame(updateScroll);
            } else {
                cancelScroll();
            }
        };
        scrollFrame.current = window.requestAnimationFrame(updateScroll);
    };

    return (
        <section
            id="featureList"
            style={{ overflowAnchor: 'none' }}
            className="scroll-mt-[10.4rem] bg-[#fdf7f2] pb-[9rem] pt-[9rem] lg:pb-[12rem] lg:pt-[12rem] xxl:pb-[16rem] xxl:pt-[18rem]"
        >
            <Container>
                <div className="mb-[5.6rem] max-w-[80rem] text-left xxl:mb-[7.2rem]">
                    <Headline level="1" tag="h2" align="left">
                        {t('pages.homepage.features.title')}
                    </Headline>
                    <p className="text-[1.8rem] leading-[1.6] text-web-copy">
                        <Trans i18nKey="pages.homepage.features.subtitle" components={{ strong: <strong /> }} />
                    </p>
                </div>
                <div className="grid items-start gap-[3rem] vl:grid-cols-[minmax(0,0.65fr)_minmax(0,1.35fr)] vl:gap-[4rem]">
                    <div className="border-t border-[#d9cdc5]">
                        {features.map((feature) => {
                            const active = selected === feature.key;

                            return (
                                <div key={feature.key} className="border-b border-[#d9cdc5]">
                                    <button
                                        type="button"
                                        aria-expanded={active}
                                        aria-controls={
                                            active
                                                ? `feature-preview-desktop feature-preview-mobile-${feature.key}`
                                                : undefined
                                        }
                                        onClick={(event) => {
                                            if (active) return;
                                            scrollToFeature(event.currentTarget);
                                            setSelected(feature.key);
                                        }}
                                        className={cn(
                                            'group relative isolate scroll-mt-[8rem] overflow-hidden transition-colors duration-200 ease-out motion-reduce:transition-none flex w-full items-start gap-[1.6rem] py-[2rem] text-left focus-visible:rounded-[0.5rem] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary sm:gap-[2rem] sm:py-[2.2rem]',
                                            active
                                                ? 'text-brand-secondary'
                                                : 'text-web-copy hover:text-brand-secondary',
                                        )}
                                    >
                                        <motion.span
                                            aria-hidden="true"
                                            initial={false}
                                            animate={{ x: 0, scale: 0.8 }}
                                            transition={{ duration: reducedMotion ? 0 : 0.3, ease: 'easeOut' }}
                                            className="pointer-events-none absolute bottom-[-1.8rem] right-0 -z-[1] origin-bottom-right select-none pr-[0.12em] text-[8.4rem] font-bold tabular-nums leading-none tracking-[-0.07em] text-[#d9cdc5]/30"
                                        >
                                            {feature.number}
                                        </motion.span>
                                        <span className="mt-[0.2rem] shrink-0">
                                            <AccordionIndicator open={active} />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-[2rem] font-bold leading-[1.2] sm:text-[2.5rem]">
                                                {t(`pages.homepage.features.${feature.translation}.nav`)}
                                            </span>
                                            <motion.span
                                                className="block overflow-hidden"
                                                initial={false}
                                                animate={{ height: active ? 'auto' : 0, opacity: active ? 1 : 0 }}
                                                transition={{ duration: reducedMotion ? 0 : 0.25 }}
                                                aria-hidden={!active}
                                            >
                                                <span className="mt-[1.3rem] block max-w-[43rem] pb-[3.2rem]">
                                                    <span className="mb-[0.5rem] block text-[1.5rem] font-semibold leading-[1.4] text-brand-secondary">
                                                        <Trans
                                                            i18nKey={`pages.homepage.features.${feature.translation}.title`}
                                                            components={{ br: <br /> }}
                                                        />
                                                    </span>
                                                    <span className="block text-[1.35rem] font-normal leading-[1.65] text-web-copy sm:text-[1.5rem]">
                                                        {t(`pages.homepage.features.${feature.translation}.subtitle`)}
                                                    </span>
                                                </span>
                                            </motion.span>
                                        </span>
                                    </button>
                                    <div className="vl:hidden">
                                        <AnimatePresence initial={false}>
                                            {active && (
                                                <motion.div
                                                    className="overflow-hidden"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: reducedMotion ? 0 : 0.25 }}
                                                >
                                                    <div className="pb-[2.4rem]">
                                                        <FeaturePreview
                                                            id={`feature-preview-mobile-${feature.key}`}
                                                            feature={feature.key}
                                                        />
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="hidden vl:sticky vl:top-[10.4rem] vl:block">
                        <FeaturePreview id="feature-preview-desktop" feature={selected} />
                    </div>
                </div>
            </Container>
        </section>
    );
};
